import { useMemo } from 'react';
import { paletteCarte } from './palette-carte.js';
import {
  BATI_EFFACE, CLASSE_PREUVE, LIBELLE_PREUVE, LIBELLE_STYLE, ORDRE_PREUVE, RAMPE_PREUVE,
  STYLES, offreQualite, type ClasseOrigine, type StylePlan,
} from './styles-plan';
import { useThemeActif } from './theme.js';
import { AFFICHAGES, type Affichage } from './affichages-carte';
import { TRAMES_ETAT, type EtatParcelle } from './trames-etat';

/**
 * LA LÉGENDE DU PLAN — dockée, avec le basculeur du plan qu'elle décrit.
 *
 * ⚠️⚠️ ELLE A QUITTÉ LA TOILE — D.943. Posée en absolu sur la carte, elle
 * recouvrait le plan en bas à droite, aux mêmes coordonnées qu'une autre
 * superposition ; dockée dans la zone de grille `legende`, elle pousse au lieu
 * de couvrir, et la replier REND sa place à la colonne.
 *
 * ⚠️ LE BASCULEUR « ÉTAT / QUALITÉ » VIT ICI, PAS DANS UN COIN DE LA TOILE.
 * Une légende décrit UN plan ; le bouton qui choisit ce plan et la liste qui
 * le décrit doivent changer ensemble — séparés, ils divergent au premier
 * ajout d'état (c'est le défaut « maille (présomptive) » déjà payé ici).
 *
 * ⚠️ `<details>` NATIF : le repli est un geste clavier standard, l'état est
 * porté par l'élément, et aucun JavaScript ne décide de ce qui est visible.
 */
export function LegendeCarte(
  { stylePlan, onStylePlan, affichage, onAffichage, couverture, zoom }:
  {
    stylePlan: StylePlan;
    onStylePlan: (s: StylePlan) => void;
    /** PC§27 — l'affichage choisi (cadastral/3D/satellite/orthophoto). */
    affichage: Affichage;
    onAffichage: (a: Affichage) => void;
    couverture?: {
      origines?: Record<string, number>;
      batimentsNonRattaches?: number;
    } | null;
    zoom: number;
  },
) {
  const theme = useThemeActif();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const P = useMemo(() => paletteCarte(), [theme]);
  const fondCarte: 'clair' | 'sombre' = theme === 'light' ? 'clair' : 'sombre';
  const enQualite = stylePlan === 'qualite';

  /**
   * ⚠️⚠️ LE BASCULEUR S'OFFRE-T-IL ? Pas « la vue est-elle juste » — elle
   * l'est toujours — mais « distingue-t-elle quelque chose ». Mesuré en
   * production le 22/09/2026 : 1 761 150 parcelles sur 1 761 157 sont en
   * `derive_bati`, soit 99,9996 %. La vue qualité peindrait une seule couleur
   * sur tout le pays : vraie, et inutile ; pire, elle laisserait croire qu'on a
   * mesuré la qualité du parcellaire alors qu'on n'a mesuré personne.
   *
   * ⚠️ ET « JE N'AI PAS SU LIRE » NE SE CONFOND PAS AVEC « TOUT EST PAREIL ».
   * `offreQualite` rend trois causes distinctes — inconnu, vide, dégénéré —
   * parce que les trois se présenteraient sinon comme le même écran muet, dont
   * le lecteur tirerait des conclusions opposées.
   */
  const verdictQualite = useMemo(() => {
    const brut = couverture?.origines;
    if (brut === undefined) return offreQualite(null);
    const parClasse: Partial<Record<ClasseOrigine, number>> = {};
    for (const [origine, n] of Object.entries(brut)) {
      const classe = (CLASSE_PREUVE as Record<string, ClasseOrigine>)[origine];
      if (classe !== undefined) parClasse[classe] = (parClasse[classe] ?? 0) + n;
    }
    return offreQualite(parClasse);
  }, [couverture?.origines]);

  return (
    <details className="carte-legende" open>
      <summary>Légende</summary>

      {/* ⚠️ PC§27 — LES QUATRE AFFICHAGES DU CONTRAT, et pas un de plus que la
          matière : satellite et orthophoto se montrent ÉTEINTS tant qu'aucune
          imagerie n'est versée, leur motif EN CLAIR — une capacité offerte qui
          n'allume rien se lit « la carte est cassée », une capacité cachée se
          lit « la demande n'a pas été traitée ». */}
      <span className="jeton-groupe" role="group" aria-label="Affichage">
        {AFFICHAGES.map((m) => (
          <button
            key={m.cle}
            type="button"
            className="jeton-bascule"
            disabled={!m.offert}
            aria-pressed={affichage === m.cle}
            onClick={() => m.offert && onAffichage(m.cle)}
          >{m.libelle}</button>
        ))}
      </span>
      {AFFICHAGES.filter((m) => !m.offert).map((m) => (
        /* Le motif est en clair, pas en infobulle — une information portée
           par le seul `title` n'existe pas au clavier (leçon D.951). */
        <span key={m.cle} className="jeton avert">{m.libelle} — {m.motif}</span>
      ))}

      {verdictQualite.offerte ? (
        <span className="jeton-groupe" role="group" aria-label="Plan affiché">
          {STYLES.map((st) => (
            <button
              key={st}
              type="button"
              className="jeton-bascule"
              aria-pressed={stylePlan === st}
              onClick={() => onStylePlan(st)}
            >{LIBELLE_STYLE[st]}</button>
          ))}
        </span>
      ) : (
        /* ⚠️ Le motif est en clair, pas seulement en infobulle — refus D.951 :
           une information portée par le seul `title` n'existe pas au clavier. */
        <span className="jeton avert">{verdictQualite.court} — {verdictQualite.motif}</span>
      )}

      {/**
        * ⚠️ LA LÉGENDE SUIT LE PLAN AFFICHÉ. Une légende qui décrit l'autre
        * style est pire qu'une absence de légende : elle est CRUE. C'est le
        * même défaut que « maille (présomptive) » nommant le statut `projet`,
        * et que « bâti — non rattaché » annonçant une classe jamais dessinée.
        */}
      {enQualite ? (
        <>
          <strong>Origine de la géométrie</strong>
          {ORDRE_PREUVE.map((classe) => (
            <span key={classe}>
              <i style={{ background: RAMPE_PREUVE[fondCarte][classe] }} />
              {LIBELLE_PREUVE[classe]}
            </span>
          ))}
          <strong>Bâti</strong>
          <span>
            <i style={{ background: BATI_EFFACE[fondCarte].corps,
                        borderColor: BATI_EFFACE[fondCarte].trait }} />
            repère seulement — ce plan porte sur les limites
          </span>
        </>
      ) : (
        <>
          <strong>Parcelles</strong>
          {/**
            * ⚠️⚠️ LES QUATRE ÉTATS VIENNENT DE LA TABLE UNIQUE — D.947. La
            * carte peint `TRAMES_ETAT`, la légende la LIT : deux listes
            * divergeraient au premier état. L'échantillon montre la TRAME
            * (currentColor, donc la teinte du jeton), et le libellé NOMME la
            * trame et la couleur — un plan photocopié reste décodable, et un
            * libellé qui ment sur sa couleur rougit au banc.
            */}
          {(Object.keys(TRAMES_ETAT) as EtatParcelle[]).map((e) => {
            const t = TRAMES_ETAT[e];
            const couleur = e === 'active' ? P.statut.active
              : e === 'en_instruction' ? P.statut.enInstruction
              : e === 'suspendue' ? P.statut.suspendue : P.maille;
            return (
              <span key={e}>
                <i className={`trame-echantillon trame-${t.trame}`} style={{ color: couleur }} />
                {t.libelle} — {t.trame}, {t.couleurNom}
              </span>
            );
          })}
          <strong>Bâti</strong>
          <span><i style={{ background: P.bati }} />rattaché</span>
          {/* ⚠️⚠️ UNE DISTINCTION VIDE SE DIT VIDE. Mesuré le 22/09/2026 :
              `count(*) filter (where parcelle_id is null)` rend **0 sur
              1 744 535** — `rattache` est vrai partout, et les deux couches
              filtrées sur son contraire ne dessinent rien. Sans cette mention,
              un agent lit que la carte sépare les deux cas et conclut de
              l'absence de tracé qu'il n'y a pas d'emprise orpheline dans son
              quartier. La couche reste : la distinction est juste, elle est
              sans objet AUJOURD'HUI, et le premier import la remplira. */}
          <span className={couverture?.batimentsNonRattaches === 0 ? 'sans-objet' : undefined}>
            <i className="pave-presume" style={{ borderColor: P.batiNon }} />
            non rattaché
            {couverture?.batimentsNonRattaches === 0
              ? ' — aucune au registre'
              : couverture?.batimentsNonRattaches !== undefined
                ? ` — ${couverture.batimentsNonRattaches.toLocaleString('fr-FR')}`
                : ''}
          </span>
          {/* ⚠️ LA LÉGENDE DIT LA PRÉSOMPTION, sinon la nuance de gris n'est
              qu'une nuance de gris. C'est elle qui empêche de lire une ville
              entièrement présumée comme une ville relevée. */}
          <span><i style={{ background: P.volumeReleve }} />volume relevé (z15+)</span>
          <span><i className="pave-presume" style={{ borderColor: P.volumePresume }} />volume présumé — hauteur non relevée</span>
        </>
      )}
      {/* ⚠️ LE DÉCOUPAGE ET LA VOIRIE SONT LES MÊMES DANS LES DEUX PLANS, et
          restent donc HORS du ternaire. Recopier ces lignes dans les deux
          branches, c'est le motif de la légende qui retapait à la main les
          couleurs du style : deux listes sans aucun moyen de rester
          d'accord. Et ces traits ne se distinguent plus par la teinte mais par
          leur couple (épaisseur, motif) — ils survivent donc au changement de
          plan sans rien perdre. */}
      <strong>Découpage</strong>
      {/* ⚠️ DEUX LIMITES QUI NE FONT PAS LE MÊME OFFICE, ET QUI SE
          RESSEMBLAIENT. La collectivité est le cadre DANS LEQUEL un code de
          section est unique — 1 515 codes sur 1 552 sont portés par
          plusieurs d'entre elles. La limite administrative, elle, ne sert
          qu'à se repérer : elle vient d'un AUTRE registre (ter.division,
          462 lignes OSM) dont aucune section ne dépend. Les confondre fait
          lire « AAA » comme un identifiant. */}
      <span><i className="trait-collectivite" style={{ borderTopColor: P.collectivite }} />
        collectivité — cadre du cadastre</span>
      <span><i className="trait-admin" style={{ borderTopColor: P.division[0] }} />limite administrative (repère)</span>
      {/* ⚠️ SANS CES DEUX LIGNES, LE TURQUOISE N'EST QU'UNE COULEUR : 99,5 %
          des sections sont tracées au carroyage, pas relevées sur le terrain. */}
      <span><i className="trait-bornee" style={{ borderTopColor: P.section }} />section — bornée par les rues</span>
      <span><i className="trait-section" style={{ borderTopColor: P.section }} />section — tracée au carroyage</span>
      <strong>Voirie</strong>
      <span><i style={{ background: P.voie[0] }} />structurante</span>
      <span><i style={{ background: P.voie[1] }} />desserte</span>
      <span><i style={{ background: P.voie[2] }} />locale</span>
      {/* ⚠️ DIRE À QUEL ZOOM CHAQUE RANG ARRIVE. Sans cette mention, « pas de
          rue dans ce quartier » et « les rues locales ne sont pas encore
          servies à ce niveau » se ressemblent trait pour trait. */}
      <em className="carte-legende-note">
        {zoom < 12 ? 'À ce zoom, seule la voirie structurante est servie.'
         : zoom < 14 ? 'Voirie structurante et de desserte. Le local arrive au zoom 14.'
         : 'Voirie complète. Survolez une rue pour lire son nom.'}
      </em>
    </details>
  );
}
